import React from 'react';

const VerificationBadges = ({ user, size = 'medium', showTooltip = true }) => {
  const sizes = {
    small: {
      badge: '16px',
      text: '10px',
      gap: '4px'
    },
    medium: {
      badge: '20px',
      text: '12px',
      gap: '6px'
    },
    large: {
      badge: '24px',
      text: '14px',
      gap: '8px'
    }
  };

  const currentSize = sizes[size];

  const verificationTypes = [
    {
      key: 'email',
      icon: '✉️',
      label: 'Email Verified',
      color: '#4CAF50',
      verified: user?.verificationStatus?.email || false
    },
    {
      key: 'phone',
      icon: '📱',
      label: 'Phone Verified',
      color: '#2196F3',
      verified: user?.verificationStatus?.phone || false
    },
    {
      key: 'social',
      icon: '🔗',
      label: 'Social Media Linked',
      color: '#FF9800',
      verified: user?.verificationStatus?.social || false
    },
    {
      key: 'identity',
      icon: '🆔',
      label: 'Identity Verified',
      color: '#9C27B0',
      verified: user?.verificationStatus?.identity || false
    }
  ];

  const verifiedTypes = verificationTypes.filter(type => type.verified);
  const verificationLevel = verifiedTypes.length;

  // Trust score badge
  const getTrustScoreBadge = (trustScore) => {
    if (trustScore >= 80) return { label: 'Highly Trusted', color: '#4CAF50', icon: '🌟' };
    if (trustScore >= 60) return { label: 'Trusted', color: '#8BC34A', icon: '⭐' };
    if (trustScore >= 40) return { label: 'Verified', color: '#FF9800', icon: '✓' };
    if (trustScore >= 20) return { label: 'Basic', color: '#FFC107', icon: '○' };
    return { label: 'New', color: '#9E9E9E', icon: '?' };
  };

  const trustScore = user?.trustScore || 0;
  const trustBadge = getTrustScoreBadge(trustScore);

  if (verificationLevel === 0 && trustScore < 20) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: currentSize.gap }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 6px',
            backgroundColor: '#f5f5f5',
            borderRadius: '12px',
            fontSize: currentSize.text,
            color: '#666'
          }}
          title={showTooltip ? 'New user - no verification' : undefined}
        >
          <span>👤</span>
          <span>New User</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: currentSize.gap,
      flexWrap: 'wrap'
    }}>
      {/* Trust Score Badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 6px',
          backgroundColor: trustBadge.color,
          color: 'white',
          borderRadius: '12px',
          fontSize: currentSize.text,
          fontWeight: '600'
        }}
        title={showTooltip ? `${trustBadge.label} User (${trustScore}/100)` : undefined}
      >
        <span>{trustBadge.icon}</span>
        <span>{trustBadge.label}</span>
      </div>

      {/* Verification Level Badge */}
      {verificationLevel > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 6px',
            backgroundColor: verificationLevel >= 3 ? '#4CAF50' : 
                           verificationLevel >= 2 ? '#2196F3' : '#FF9800',
            color: 'white',
            borderRadius: '12px',
            fontSize: currentSize.text,
            fontWeight: '600'
          }}
          title={showTooltip ? `${verificationLevel}/4 verifications completed` : undefined}
        >
          <span>🛡️</span>
          <span>L{verificationLevel}</span>
        </div>
      )}

      {/* Individual Verification Badges */}
      {verifiedTypes.map((type) => (
        <div
          key={type.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: currentSize.badge,
            height: currentSize.badge,
            backgroundColor: type.color,
            borderRadius: '50%',
            fontSize: currentSize.text,
            color: 'white',
            fontWeight: 'bold'
          }}
          title={showTooltip ? type.label : undefined}
        >
          {type.icon}
        </div>
      ))}

      {/* Social Media Accounts */}
      {user?.socialAccounts && user.socialAccounts.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            padding: '2px 6px',
            backgroundColor: '#f5f5f5',
            borderRadius: '12px',
            fontSize: currentSize.text,
            color: '#666'
          }}
          title={showTooltip ? `${user.socialAccounts.length} social account(s) linked` : undefined}
        >
          <span>🔗</span>
          <span>{user.socialAccounts.length}</span>
        </div>
      )}

      {/* Account Flags Warning */}
      {user?.accountFlags && user.accountFlags.some(flag => !flag.resolved) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 6px',
            backgroundColor: '#f44336',
            color: 'white',
            borderRadius: '12px',
            fontSize: currentSize.text,
            fontWeight: '600'
          }}
          title={showTooltip ? 'Account has unresolved flags' : undefined}
        >
          <span>⚠️</span>
          <span>Flagged</span>
        </div>
      )}
    </div>
  );
};

export default VerificationBadges;